import { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';

type ThemeContextType = {
  isDark: boolean;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Get theme from localStorage or system preference on mount
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    const initialDarkMode = savedTheme === 'dark' || (!savedTheme && systemPrefersDark);
    setIsDark(initialDarkMode);
    
    // Apply theme to document
    document.documentElement.classList.toggle('dark', initialDarkMode);
  }, []);

  useEffect(() => {
    // Update document class and localStorage when theme changes
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prevDark => !prevDark);
  };

  // Create Material-UI theme based on dark mode state
  const muiTheme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      ...(isDark
        ? {
            // Dark theme colors
            primary: {
              main: '#60A5FA', // blue-400
            },
            secondary: {
              main: '#A78BFA', // violet-400
            },
            background: {
              default: '#0F172A', // slate-900
              paper: '#1E293B', // slate-800
            },
            text: {
              primary: '#F1F5F9', // slate-100
              secondary: '#CBD5E1', // slate-300
            },
          }
        : {
            // Light theme colors
            primary: {
              main: '#2563EB', // blue-600
            },
            secondary: {
              main: '#7C3AED', // violet-600
            },
            background: {
              default: '#FFFFFF',
              paper: '#F8FAFC', // slate-50
            },
            text: {
              primary: '#0F172A', // slate-900
              secondary: '#475569', // slate-600
            },
          }),
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: isDark ? '#475569' : '#CBD5E1', // slate-600 : slate-300
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? '#CBD5E1' : '#475569', // slate-300 : slate-600
            '&.Mui-focused': {
              color: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#475569' : '#CBD5E1', // slate-600 : slate-300
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#374151' : '#E5E7EB', // gray-700 : gray-200
            color: isDark ? '#F9FAFB' : '#1F2937', // gray-50 : gray-800
            '&:hover': {
              backgroundColor: isDark ? '#4B5563' : '#D1D5DB', // gray-600 : gray-300
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1E293B' : '#FFFFFF', // slate-800 : white
            color: isDark ? '#F1F5F9' : '#0F172A', // slate-100 : slate-900
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: isDark ? '#F1F5F9' : '#0F172A', // slate-100 : slate-900
            '&:hover': {
              backgroundColor: isDark ? '#334155' : '#F1F5F9', // slate-700 : slate-100
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? '#475569' : '#E2E8F0', // slate-600 : slate-200
              '&:hover': {
                backgroundColor: isDark ? '#334155' : '#CBD5E1', // slate-700 : slate-300
              },
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
          },
          track: {
            color: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
          },
          thumb: {
            color: isDark ? '#60A5FA' : '#2563EB', // blue-400 : blue-600
          },
          rail: {
            color: isDark ? '#475569' : '#CBD5E1', // slate-600 : slate-300
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: isDark ? '#94A3B8' : '#64748B', // slate-400 : slate-500
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}